import { forwardRef } from 'react';

/**
 * The trigger input shared by every picker: leading icon, value, optional clear.
 * Date fields are read-only (typing across dd-MM vs MM-dd formats is ambiguous);
 * the time field opts into typing via `editable`, where HH:mm is unambiguous.
 */
const PickerField = forwardRef(({
    icon,
    value,
    placeholder,
    open,
    popupId,
    disabled,
    invalid,
    clearable,
    editable = false,
    size = 'md',
    className = '',
    ariaLabel,
    onOpen,
    onClear,
    onInputChange,
    onInputCommit,
}, ref) => {
    const handleKeyDown = (event) => {
        if (event.key === 'ArrowDown' || (!editable && (event.key === 'Enter' || event.key === ' '))) {
            event.preventDefault();
            onOpen();
            return;
        }
        if (editable && event.key === 'Enter') {
            event.preventDefault();
            onInputCommit?.(event.target.value);
        }
    };

    return (
        <div
            ref={ref}
            className={[
                'dt-field',
                `dt-field--${size}`,
                open ? 'is-open' : '',
                invalid ? 'is-invalid' : '',
                disabled ? 'is-disabled' : '',
                className,
            ].filter(Boolean).join(' ')}
        >
            <span className={`dt-field__icon fas ${icon}`} aria-hidden="true" />

            <input
                type="text"
                className="dt-field__input"
                value={value}
                placeholder={placeholder}
                disabled={disabled}
                readOnly={!editable}
                aria-label={ariaLabel}
                // combobox rather than the implicit textbox role: textbox does not
                // support aria-expanded, and this input does own a popup.
                role="combobox"
                aria-haspopup="dialog"
                aria-expanded={open}
                aria-controls={open ? popupId : undefined}
                autoComplete="off"
                onChange={editable ? (event => onInputChange?.(event.target.value)) : undefined}
                onBlur={editable ? (event => onInputCommit?.(event.target.value)) : undefined}
                onMouseDown={disabled ? undefined : onOpen}
                onKeyDown={disabled ? undefined : handleKeyDown}
            />

            {clearable && value && !disabled && (
                <button
                    type="button"
                    className="dt-field__clear"
                    aria-label="Clear"
                    onMouseDown={event => event.stopPropagation()}
                    onClick={onClear}
                >
                    <span className="fas fa-xmark" aria-hidden="true" />
                </button>
            )}
        </div>
    );
});

PickerField.displayName = 'PickerField';

export default PickerField;
